/**
 * "You might like" data for Book Details — local catalog overlap first,
 * an Open Library subject search fallback when there aren't enough local
 * matches (see utils/similarBooks.js and services/openLibraryService.js's
 * searchBySubject). Each candidate gets a short "why" line, in the same
 * spirit as data.js's RECOMMENDED.reason field.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchBySubject } from '@/services/openLibraryService';
import { getSimilarBooks } from '../utils/similarBooks';

const MIN_LOCAL_MATCHES = 4;

export function useSimilarBooks(book, { limit = 8 } = {}) {
  const local = useMemo(() => (book ? getSimilarBooks(book, { limit }) : []), [book, limit]);
  const primarySubject = book?.subjects?.[0] ?? null;
  const needsFallback = Boolean(book) && local.length < MIN_LOCAL_MATCHES && Boolean(primarySubject);

  const { data: remote, isFetching } = useQuery({
    queryKey: ['similar-books-subject', primarySubject],
    queryFn: ({ signal }) => searchBySubject(primarySubject, { limit: limit - local.length, signal }),
    enabled: needsFallback,
    staleTime: 10 * 60_000,
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const books = useMemo(() => {
    const localIds = new Set(local.map((b) => b.id));
    const combined = [...local, ...(remote ?? []).filter((b) => !localIds.has(b.id))];
    return combined.slice(0, limit);
  }, [local, remote, limit]);

  const reasonFor = (candidate) => {
    if (!book) return null;
    if (candidate.friendsReading > 0) return `Readers of ${book.title} also loved this`;
    const sharedSubject = (candidate.subjects ?? candidate.genres ?? [])[0];
    return sharedSubject ? `Because you liked ${sharedSubject}` : `More like ${book.title}`;
  };

  return { books, isLoading: needsFallback && isFetching, reasonFor };
}
